import React, { useState } from 'react';
import { 
  ChartBarIcon, 
  ClockIcon, 
  PhoneIcon, 
  CurrencyDollarIcon,
  CalendarIcon,
  ChartPieIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { useAnalyticsPage } from './useAnalyticsPage';
import TimeRangeFilter from '../../components/filters/TimeRangeFilter';
import './AnalyticsTabs.css';

const PIE_COLORS = [
  '#06b6d4', // cyan
  '#8b5cf6', // purple
  '#10b981', // green
  '#f59e0b', // orange
  '#ef4444', // red
  '#3b82f6', // blue
  '#ec4899', // pink
  '#14b8a6', // teal
];

/** Format date string (YYYY-MM-DD) for chart axis/tooltips */
function formatChartDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Format seconds as whole numbers for display (e.g. 65s -> 1m 5s) */
function formatDurationOneDecimal(seconds: number): string {
  const rounded = Math.round(seconds);
  if (rounded >= 60) {
    const mins = Math.floor(rounded / 60);
    const secs = rounded % 60;
    return `${mins}m ${secs}s`;
  }
  return `${rounded}s`;
}

/** Hour number to readable time (e.g. 14 -> "2 PM") */
function formatHour(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}


// Custom dot for line charts
const CustomDot = (props: any) => {
  const { cx, cy, stroke, value } = props;
  if (value === undefined) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={6} fill={stroke} opacity={0.3} />
      <circle cx={cx} cy={cy} r={3} fill={stroke} stroke="#fff" strokeWidth={1.5} />
    </g>
  );
};

// Custom pie label – high-contrast text so it’s visible on every segment
const CustomPieLabel = (props: any) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, name, value, percent } = props;
  const RADIAN = Math.PI / 180;
  const radius = (innerRadius + outerRadius) / 2 + (outerRadius - innerRadius) * 0.3;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const pct = percent != null ? ((percent * 100).toFixed(0)) : '0';
  const text = `${name}: ${value} (${pct}%)`;
  return (
    <g>
      <text
        x={x}
        y={y}
        fill="#ffffff"
        stroke="#0f172a"
        strokeWidth={3}
        strokeLinejoin="round"
        strokeLinecap="round"
        paintOrder="stroke fill"
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontFamily: 'inherit', fontSize: '12px', fontWeight: 600 }}
      >
        {text}
      </text>
    </g>
  );
};

// Chart subtitle for clarity
const ChartSubtitle = ({ children }: { children: React.ReactNode }) => (
  <p className="text-gray-400 text-xs mt-0.5 mb-2">{children}</p>
);

// Custom tooltip with optional value formatter
const CustomTooltip = ({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter = (v: number) => String(v),
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  labelFormatter?: (label: string) => string;
  valueFormatter?: (v: number) => string;
}) => {
  if (!active || !payload || !payload.length) return null;
  const title = labelFormatter ? labelFormatter(label ?? '') : label;
  return (
    <div className="bg-slate-900/95 backdrop-blur-xl border border-cyan-500/30 rounded-lg p-3 shadow-2xl min-w-[120px]">
      {title && <p className="text-white font-semibold mb-2 text-sm">{title}</p>}
      {payload.map((entry: any, index: number) => (
        <div key={index} className="flex items-center justify-between gap-4 text-xs">
          <span className="flex items-center">
            <span className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: entry.color }} />
            <span className="text-gray-300">{entry.name}:</span>
          </span>
          <span className="font-semibold text-white">{valueFormatter(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

const AnalyticsPage: React.FC = () => {
  const {
    summary,
    callLogs,
    trendsData,
    costAnalysis,
    intentPerformance,
    loading,
    error,
    timeRange,
    setTimeRange,
    fetchAnalytics,
  } = useAnalyticsPage();

  const [activeTab, setActiveTab] = useState<'overview' | 'trends' | 'costs'>('overview');

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -right-32 sm:-right-64 -top-32 sm:-top-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full"></div>
        <div className="absolute -left-32 sm:-left-64 -bottom-32 sm:-bottom-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full"></div>
      </div>
      
      <div className="relative px-2 sm:px-3 lg:px-8 py-3 sm:py-4 lg:py-6 xl:py-8 max-w-[1600px]">
        {/* Header */}
        <div className="animate-in slide-in-from-top duration-700 mb-4 sm:mb-6 lg:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
            <div>
              <div className="flex items-center space-x-2 sm:space-x-3 mb-1 sm:mb-2">
                <div className="p-1.5 sm:p-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg shadow-lg">
                  <ChartBarIcon className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-white" />
                </div>
                <h1 className="text-lg sm:text-xl lg:text-3xl xl:text-4xl font-bold text-slate-50">
                  Analytics Dashboard
                </h1>
              </div>
              <p className="text-gray-300 text-xs sm:text-sm lg:text-lg">
                Track your campaign performance with detailed insights
              </p>
            </div>
            
            {/* Time Range Selector */}
            <div className="mt-3 sm:mt-0">
              <TimeRangeFilter value={timeRange} onChange={setTimeRange} />
            </div>
          </div>

          {/* Tab Navigation - same style as Admin Panel (underline, no pill) */}
          <nav className="analytics-tabs" aria-label="Analytics sections">
            {[
              { id: 'overview', label: 'Overview', icon: ChartPieIcon },
              { id: 'trends', label: 'Trends', icon: ArrowTrendingUpIcon },
              { id: 'costs', label: 'Credit Analysis', icon: CurrencyDollarIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as any)}
                className={`analytics-tab ${activeTab === tab.id ? 'analytics-tab-active' : ''}`}
              >
                <tab.icon className="h-4 w-4 flex-shrink-0" aria-hidden />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 mb-4 sm:mb-6 rounded-lg bg-red-500/10 border border-red-500/25 text-red-300 text-sm">
            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" className="flex-shrink-0">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
            </svg>
            <span>{error}</span>
            <button
              onClick={() => fetchAnalytics()}
              className="ml-auto text-xs px-3 py-1 rounded bg-red-500/20 hover:bg-red-500/30 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
          <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl p-3 sm:p-4 rounded-lg shadow-2xl border border-slate-700/50 hover:border-cyan-500/50 transition-all duration-300">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-slate-300 text-xs font-medium">Total Calls</h3>
              <PhoneIcon className="h-4 w-4 text-cyan-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-white mb-0.5">
              {loading && !summary ? (
                <span className="inline-block w-12 h-6 bg-slate-600/50 rounded animate-pulse" />
              ) : (
                summary?.total_calls || 0
              )}
            </p>
            <p className="text-xs text-gray-400">
              {summary?.successful_calls || 0} successful
            </p>
          </div>

          <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl p-3 sm:p-4 rounded-lg shadow-2xl border border-slate-700/50 hover:border-green-500/50 transition-all duration-300">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-slate-300 text-xs font-medium">Success Rate</h3>
              <ArrowTrendingUpIcon className="h-4 w-4 text-green-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-green-400 mb-0.5">
              {loading && !summary ? (
                <span className="inline-block w-12 h-6 bg-slate-600/50 rounded animate-pulse" />
              ) : (
                `${summary?.success_rate || 0}%`
              )}
            </p>
            <p className="text-xs text-gray-400">
              {summary?.failed_calls || 0} failed
            </p>
          </div>

          <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl p-3 sm:p-4 rounded-lg shadow-2xl border border-slate-700/50 hover:border-blue-500/50 transition-all duration-300">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-slate-300 text-xs font-medium">Avg Duration</h3>
              <ClockIcon className="h-4 w-4 text-blue-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-white mb-0.5">
              {loading && !summary ? (
                <span className="inline-block w-12 h-6 bg-slate-600/50 rounded animate-pulse" />
              ) : summary?.average_duration != null ? (
                formatDurationOneDecimal(summary.average_duration)
              ) : (
                '0s'
              )}
            </p>
            <p className="text-xs text-gray-400">per call</p>
          </div>

          <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl p-3 sm:p-4 rounded-lg shadow-2xl border border-slate-700/50 hover:border-yellow-500/50 transition-all duration-300">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-slate-300 text-xs font-medium">Total Credits</h3>
              <CurrencyDollarIcon className="h-4 w-4 text-yellow-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-white mb-0.5">
              {loading && !costAnalysis ? (
                <span className="inline-block w-12 h-6 bg-slate-600/50 rounded animate-pulse" />
              ) : (
                `${costAnalysis?.total_credits ?? 0}`
              )}
            </p>
            <p className="text-xs text-gray-400">
              {(costAnalysis?.average_credits_per_call ?? 0).toFixed(2)} credits per call
            </p>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Call Status Distribution */}
              <div className="bg-gradient-to-br from-slate-800/40 via-slate-800/30 to-slate-900/60 backdrop-blur-xl rounded-xl shadow-2xl border border-slate-700/50 hover:border-cyan-500/30 transition-all duration-300 p-4 group">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <div className="p-1.5 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-lg mr-2 group-hover:from-cyan-500/30 group-hover:to-blue-500/30 transition-all">
                    <ChartPieIcon className="h-4 w-4 text-cyan-400" />
                  </div>
                  Call Status Distribution
                </h3>
                <ChartSubtitle>Breakdown of how your calls ended (ended, failed, in progress, etc.)</ChartSubtitle>
                {loading || !trendsData ? (
                  <div className="h-48 flex items-center justify-center">
                    <div className="relative">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                      <div className="absolute inset-0 animate-ping rounded-full h-8 w-8 border border-cyan-500/30"></div>
                    </div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <defs>
                        {PIE_COLORS.map((color, index) => (
                          <linearGradient key={`gradient-${index}`} id={`pieGradient${index}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={color} stopOpacity={1} />
                            <stop offset="100%" stopColor={color} stopOpacity={0.7} />
                          </linearGradient>
                        ))}
                      </defs>
                      <Pie
                        data={Object.entries(trendsData.status_distribution).map(([name, value]) => ({
                          name: name.charAt(0).toUpperCase() + name.slice(1),
                          value,
                        }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={<CustomPieLabel />}
                        outerRadius={80}
                        innerRadius={40}
                        fill="#8884d8"
                        dataKey="value"
                        animationBegin={0}
                        animationDuration={800}
                      >
                        {Object.keys(trendsData.status_distribution).map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={`url(#pieGradient${index % PIE_COLORS.length})`}
                            stroke="rgba(15, 23, 42, 0.8)"
                            strokeWidth={2}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        content={
                          <CustomTooltip
                            valueFormatter={(v) => {
                              const total = Object.values(trendsData.status_distribution).reduce((a: number, b: number) => a + b, 0);
                              const pct = total ? ((v / total) * 100).toFixed(1) : '0';
                              return `${v} calls (${pct}%)`;
                            }}
                          />
                        }
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Call Duration Distribution */}
              <div className="bg-gradient-to-br from-slate-800/40 via-slate-800/30 to-slate-900/60 backdrop-blur-xl rounded-xl shadow-2xl border border-slate-700/50 hover:border-purple-500/30 transition-all duration-300 p-4 group">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <div className="p-1.5 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg mr-2 group-hover:from-purple-500/30 group-hover:to-pink-500/30 transition-all">
                    <ClockIcon className="h-4 w-4 text-purple-400" />
                  </div>
                  Call Duration Distribution
                </h3>
                <ChartSubtitle>How many calls fall into each length bucket (e.g. 0–30 sec, 1–2 min)</ChartSubtitle>
                {loading || !callLogs || callLogs.length === 0 ? (
                  <div className="h-48 flex items-center justify-center">
                    {loading ? (
                      <div className="relative">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                        <div className="absolute inset-0 animate-ping rounded-full h-8 w-8 border border-purple-500/30"></div>
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">No call data available</p>
                    )}
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={(() => {
                        const ranges = [
                          { range: '0–30s', min: 0, max: 30, count: 0 },
                          { range: '31s–1m', min: 31, max: 60, count: 0 },
                          { range: '1–2m', min: 61, max: 120, count: 0 },
                          { range: '2–5m', min: 121, max: 300, count: 0 },
                          { range: '5m+', min: 301, max: Infinity, count: 0 },
                        ];
                        callLogs.forEach(call => {
                          if (call.startedAt && call.endedAt) {
                            const duration = Math.round((new Date(call.endedAt).getTime() - new Date(call.startedAt).getTime()) / 1000);
                            const range = ranges.find(r => duration >= r.min && duration <= r.max);
                            if (range) range.count++;
                          }
                        });
                        return ranges.map(r => ({ range: r.range, count: r.count }));
                      })()}
                    >
                      <defs>
                        <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#a855f7" stopOpacity={1} />
                          <stop offset="100%" stopColor="#ec4899" stopOpacity={0.8} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                      <XAxis 
                        dataKey="range" 
                        stroke="#94a3b8" 
                        style={{ fontSize: '12px' }}
                        tick={{ fill: '#94a3b8' }}
                        label={{ value: 'Call length', position: 'insideBottom', offset: -5, fill: '#94a3b8', fontSize: 11 }}
                      />
                      <YAxis 
                        stroke="#94a3b8" 
                        style={{ fontSize: '12px' }}
                        tick={{ fill: '#94a3b8' }}
                        label={{ value: 'Number of calls', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 11 }}
                      />
                      <Tooltip
                        content={
                          <CustomTooltip
                            valueFormatter={(v) => `${v} call${v !== 1 ? 's' : ''}`}
                          />
                        }
                      />
                      <Bar 
                        dataKey="count" 
                        fill="url(#barGradient)" 
                        radius={[8, 8, 0, 0]} 
                        name="Calls"
                        animationBegin={0}
                        animationDuration={800}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Hourly Distribution Chart */}
            <div className="bg-gradient-to-br from-slate-800/40 via-slate-800/30 to-slate-900/60 backdrop-blur-xl rounded-xl shadow-2xl border border-slate-700/50 hover:border-blue-500/30 transition-all duration-300 p-4 group">
              <h3 className="text-base font-semibold text-white flex items-center">
                <div className="p-1.5 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-lg mr-2 group-hover:from-blue-500/30 group-hover:to-cyan-500/30 transition-all">
                  <CalendarIcon className="h-4 w-4 text-blue-400" />
                </div>
                Hourly Call Distribution
              </h3>
              <ChartSubtitle>When you make most calls (by hour of day, 12 AM–11 PM)</ChartSubtitle>
              {loading || !trendsData ? (
                <div className="h-48 flex items-center justify-center">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <div className="absolute inset-0 animate-ping rounded-full h-8 w-8 border border-blue-500/30"></div>
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={trendsData.hourly_distribution}>
                    <defs>
                      <linearGradient id="hourlyGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
                        <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.8} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                    <XAxis 
                      dataKey="hour" 
                      stroke="#94a3b8"
                      style={{ fontSize: '11px' }}
                      tick={{ fill: '#94a3b8' }}
                      tickFormatter={formatHour}
                      interval={2}
                      label={{ value: 'Time of day', position: 'insideBottom', offset: -5, fill: '#94a3b8', fontSize: 11 }}
                    />
                    <YAxis 
                      stroke="#94a3b8" 
                      style={{ fontSize: '12px' }}
                      tick={{ fill: '#94a3b8' }}
                      label={{ value: 'Number of calls', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 11 }}
                    />
                    <Tooltip 
                      content={
                        <CustomTooltip
                          labelFormatter={(hour) => formatHour(Number(hour))}
                          valueFormatter={(v) => `${v} call${v !== 1 ? 's' : ''}`}
                        />
                      }
                    />
                    <Bar 
                      dataKey="calls" 
                      fill="url(#hourlyGradient)" 
                      radius={[8, 8, 0, 0]}
                      name="Calls"
                      animationBegin={0}
                      animationDuration={800}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Intent Performance Table */}
            {intentPerformance && Object.keys(intentPerformance.intent_performance).length > 0 && (
              <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg shadow-2xl border border-slate-700/50 p-4">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <ChartBarIcon className="h-4 w-4 mr-2 text-green-400" />
                  Performance by Intent
                </h3>
                <ChartSubtitle>Success rate and average duration for each campaign or intent type</ChartSubtitle>
                <div className="overflow-x-auto scrollbar-hide">
                  <table className="w-full">
                    <thead className="bg-slate-800/30">
                      <tr>
                        <th className="px-3 py-2 text-left text-white text-xs font-semibold">Intent</th>
                        <th className="px-3 py-2 text-left text-white text-xs font-semibold">Total Calls</th>
                        <th className="px-3 py-2 text-left text-white text-xs font-semibold">Success Rate</th>
                        <th className="px-3 py-2 text-left text-white text-xs font-semibold">Avg Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(intentPerformance.intent_performance).map((intent, idx) => (
                        <tr key={idx} className="border-b border-slate-700/30 hover:bg-slate-800/30">
                          <td className="px-3 py-2 text-white text-sm">{intent.name}</td>
                          <td className="px-3 py-2 text-white text-sm">{intent.total_calls}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 bg-slate-700/50 rounded-full h-1.5">
                                <div
                                  className="bg-gradient-to-r from-green-500 to-emerald-500 h-1.5 rounded-full"
                                  style={{ width: `${intent.success_rate}%` }}
                                ></div>
                              </div>
                              <span className="text-green-400 text-xs font-medium">{intent.success_rate}%</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-white text-sm">
                            {formatDurationOneDecimal(intent.average_duration)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'trends' && (
          <div className="space-y-4">
            {/* Daily Calls Trend */}
            <div className="bg-gradient-to-br from-slate-800/40 via-slate-800/30 to-slate-900/60 backdrop-blur-xl rounded-xl shadow-2xl border border-slate-700/50 hover:border-cyan-500/30 transition-all duration-300 p-4 group">
              <h3 className="text-base font-semibold text-white flex items-center">
                <div className="p-1.5 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-lg mr-2 group-hover:from-cyan-500/30 group-hover:to-blue-500/30 transition-all">
                  <ArrowTrendingUpIcon className="h-4 w-4 text-cyan-400" />
                </div>
                Daily Call Volume
              </h3>
              <ChartSubtitle>Total, successful, and failed calls per day over the selected period</ChartSubtitle>
              {loading || !trendsData ? (
                <div className="h-48 flex items-center justify-center">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                    <div className="absolute inset-0 animate-ping rounded-full h-8 w-8 border border-cyan-500/30"></div>
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={trendsData.daily_stats}>
                    <defs>
                      <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.9}/>
                        <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.9}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorFailed" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.9}/>
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                    <XAxis 
                      dataKey="date" 
                      stroke="#94a3b8" 
                      style={{ fontSize: '11px' }}
                      tick={{ fill: '#94a3b8' }}
                      tickFormatter={formatChartDate}
                      label={{ value: 'Date', position: 'insideBottom', offset: -5, fill: '#94a3b8', fontSize: 11 }}
                    />
                    <YAxis 
                      stroke="#94a3b8" 
                      style={{ fontSize: '11px' }}
                      tick={{ fill: '#94a3b8' }}
                      label={{ value: 'Calls', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 11 }}
                    />
                    <Tooltip
                      content={
                        <CustomTooltip
                          labelFormatter={(d) => formatChartDate(d)}
                          valueFormatter={(v) => `${v} call${v !== 1 ? 's' : ''}`}
                        />
                      }
                    />
                    <Legend wrapperStyle={{ fontSize: '12px', marginTop: '18px', paddingTop: '8px', borderTop: '1px solid transparent' }} />
                    <Area 
                      type="monotone" 
                      dataKey="total" 
                      stroke="#06b6d4" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorTotal)" 
                      name="Total"
                      animationBegin={0}
                      animationDuration={1000}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="successful" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorSuccess)" 
                      name="Successful"
                      animationBegin={100}
                      animationDuration={1000}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="failed" 
                      stroke="#ef4444" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorFailed)" 
                      name="Failed"
                      animationBegin={200}
                      animationDuration={1000}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Success Rate and Duration Trends */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Success Rate Trend */}
              <div className="bg-gradient-to-br from-slate-800/40 via-slate-800/30 to-slate-900/60 backdrop-blur-xl rounded-xl shadow-2xl border border-slate-700/50 hover:border-green-500/30 transition-all duration-300 p-4 group">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <div className="p-1.5 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg mr-2 group-hover:from-green-500/30 group-hover:to-emerald-500/30 transition-all">
                    <ArrowTrendingUpIcon className="h-4 w-4 text-green-400" />
                  </div>
                  Success Rate Trend
                </h3>
                <ChartSubtitle>Share of calls that ended successfully each day (0–100%)</ChartSubtitle>
                {loading || !trendsData ? (
                  <div className="h-48 flex items-center justify-center">
                    <div className="relative">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                      <div className="absolute inset-0 animate-ping rounded-full h-8 w-8 border border-green-500/30"></div>
                    </div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={trendsData.daily_stats}>
                      <defs>
                        <linearGradient id="successGlow" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                      <XAxis 
                        dataKey="date" 
                        stroke="#94a3b8" 
                        style={{ fontSize: '11px' }}
                        tick={{ fill: '#94a3b8' }}
                        tickFormatter={formatChartDate}
                        label={{ value: 'Date', position: 'insideBottom', offset: -5, fill: '#94a3b8', fontSize: 11 }}
                      />
                      <YAxis 
                        stroke="#94a3b8" 
                        style={{ fontSize: '11px' }}
                        tick={{ fill: '#94a3b8' }}
                        domain={[0, 100]}
                        tickFormatter={(v) => `${v}%`}
                        label={{ value: 'Success rate', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 11 }}
                      />
                      <Tooltip
                        content={
                          <CustomTooltip
                            labelFormatter={(d) => formatChartDate(d)}
                            valueFormatter={(v) => `${Number(v).toFixed(1)}%`}
                          />
                        }
                      />
                      <Line 
                        type="monotone" 
                        dataKey="success_rate" 
                        stroke="#10b981" 
                        strokeWidth={3}
                        dot={<CustomDot />}
                        name="Success rate"
                        animationBegin={0}
                        animationDuration={1000}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Average Duration Trend */}
              <div className="bg-gradient-to-br from-slate-800/40 via-slate-800/30 to-slate-900/60 backdrop-blur-xl rounded-xl shadow-2xl border border-slate-700/50 hover:border-blue-500/30 transition-all duration-300 p-4 group">
                <h3 className="text-base font-semibold text-white flex items-center">
                  <div className="p-1.5 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-lg mr-2 group-hover:from-blue-500/30 group-hover:to-cyan-500/30 transition-all">
                    <ClockIcon className="h-4 w-4 text-blue-400" />
                  </div>
                  Avg Duration Trend
                </h3>
                <ChartSubtitle>Average call length per day (in seconds)</ChartSubtitle>
                {loading || !trendsData ? (
                  <div className="h-48 flex items-center justify-center">
                    <div className="relative">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      <div className="absolute inset-0 animate-ping rounded-full h-8 w-8 border border-blue-500/30"></div>
                    </div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={trendsData.daily_stats}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                      <XAxis 
                        dataKey="date" 
                        stroke="#94a3b8" 
                        style={{ fontSize: '11px' }}
                        tick={{ fill: '#94a3b8' }}
                        tickFormatter={formatChartDate}
                        label={{ value: 'Date', position: 'insideBottom', offset: -5, fill: '#94a3b8', fontSize: 11 }}
                      />
                      <YAxis 
                        stroke="#94a3b8" 
                        style={{ fontSize: '11px' }}
                        tick={{ fill: '#94a3b8' }}
                        tickFormatter={(v) => (v >= 60 ? `${Math.round(v / 60)}m` : `${v}s`)}
                        label={{ value: 'Duration', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 11 }}
                      />
                      <Tooltip
                        content={
                          <CustomTooltip
                            labelFormatter={(d) => formatChartDate(d)}
                            valueFormatter={(v) => formatDurationOneDecimal(Number(v))}
                          />
                        }
                      />
                      <Line 
                        type="monotone" 
                        dataKey="avg_duration" 
                        stroke="#3b82f6" 
                        strokeWidth={3}
                        dot={<CustomDot />}
                        name="Avg duration"
                        animationBegin={0}
                        animationDuration={1000}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'costs' && (
          <div className="space-y-4">
            {/* Credits Over Time */}
            <div className="bg-gradient-to-br from-slate-800/40 via-slate-800/30 to-slate-900/60 backdrop-blur-xl rounded-xl shadow-2xl border border-slate-700/50 hover:border-yellow-500/30 transition-all duration-300 p-4 group">
              <h3 className="text-base font-semibold text-white mb-3 flex items-center">
                <div className="p-1.5 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-lg mr-2 group-hover:from-yellow-500/30 group-hover:to-orange-500/30 transition-all">
                  <BanknotesIcon className="h-4 w-4 text-yellow-400" />
                </div>
                Daily Credits Usage
              </h3>
              {loading || !costAnalysis ? (
                <div className="h-48 flex items-center justify-center">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
                    <div className="absolute inset-0 animate-ping rounded-full h-8 w-8 border border-yellow-500/30"></div>
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={(costAnalysis.credits_by_day || []).map(item => ({
                    date: item.date,
                    credits: item.credits
                  }))}>
                    <defs>
                      <linearGradient id="colorCredits" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.9}/>
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.3} />
                    <XAxis 
                      dataKey="date" 
                      stroke="#94a3b8" 
                      style={{ fontSize: '11px' }}
                      tick={{ fill: '#94a3b8' }}
                    />
                    <YAxis 
                      stroke="#94a3b8" 
                      style={{ fontSize: '11px' }}
                      tick={{ fill: '#94a3b8' }}
                    />
                    <Tooltip 
                      content={<CustomTooltip />}
                      formatter={(value: any) => `${value} credits`}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="credits" 
                      stroke="#f59e0b" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorCredits)"
                      name="Daily Credits"
                      animationBegin={0}
                      animationDuration={1000}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Credits Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg shadow-2xl border border-slate-700/50 p-4">
                <h4 className="text-xs text-gray-400 mb-2">Total Credits Used</h4>
                <p className="text-2xl font-bold text-white mb-1">
                  {costAnalysis?.total_credits ?? 0}
                </p>
                <p className="text-xs text-gray-400">Across all calls</p>
              </div>

              <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg shadow-2xl border border-slate-700/50 p-4">
                <h4 className="text-xs text-gray-400 mb-2">Average Per Call</h4>
                <p className="text-2xl font-bold text-white mb-1">
                  {(costAnalysis?.average_credits_per_call ?? 0).toFixed(2)}
                </p>
                <p className="text-xs text-gray-400">Credits per call</p>
              </div>

              <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg shadow-2xl border border-slate-700/50 p-4">
                <h4 className="text-xs text-gray-400 mb-2">Total Calls</h4>
                <p className="text-2xl font-bold text-white mb-1">
                  {summary?.total_calls || 0}
                </p>
                <p className="text-xs text-gray-400">In selected period</p>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AnalyticsPage;
